import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import Icon from '../icon/Icon'
import { useImportedListings } from '../../_hooks/use-imported-listings'
import { useQuickPassNotifications } from '../../_hooks/use-quickpass-notifications'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { formatRelativeTime } from '../../_utilities/relative-time'
import { buildActivityFeed } from './helpers'
import {
  Section,
  SectionHeader,
  Title,
  Stamp,
  FeedList,
  Row,
  Glyph,
  Body,
  RowHeader,
  RowTitle,
  RowTime,
  RowText,
  EmptyBlock,
  ViewAllLink,
} from './styled'
import { ActivityEntry, ActivityFeedProps } from './types'

const DEFAULT_LIMIT = 6

/**
 * Chronological feed of everything that happened on the renter's
 * QuickPass — price changes, listing imports, achievements, onboarding.
 * Compact list optimised for the dashboard preview; passing a larger
 * `limit` makes it the full /activity surface.
 *
 * @param {ActivityFeedProps} props
 * @returns {JSX.Element | null}
 */
const ActivityFeed = ({
  limit = DEFAULT_LIMIT,
  shouldShow = true,
  showEmptyState = false,
}: ActivityFeedProps): JSX.Element | null => {
  const navigate = useNavigate()
  const { notifications } = useQuickPassNotifications()
  const { listings } = useImportedListings()
  const { profile } = useQuickPassProfile()

  const feed = useMemo(
    () => buildActivityFeed(notifications, listings, profile),
    [notifications, listings, profile],
  )

  if (shouldShow === false) {
    return null
  }

  if (feed.length === 0 && showEmptyState === false) {
    return null
  }

  const visible = feed.slice(0, limit)
  const hasMore = feed.length > limit

  const handleRowClick = (entry: ActivityEntry): void => {
    if (entry.linkPath !== null) {
      navigate(entry.linkPath)
    }
  }

  return (
    <Section>
      <SectionHeader>
        <Title>Recent activity</Title>
        {hasMore === true ? (
          <ViewAllLink to="/activity">View all →</ViewAllLink>
        ) : (
          <Stamp>
            {feed.length === 1 ? '1 event' : `${feed.length} events`}
          </Stamp>
        )}
      </SectionHeader>

      {visible.length === 0 ? (
        <EmptyBlock>
          As you tag listings, watch prices, and unlock achievements, this
          timeline will fill up.
        </EmptyBlock>
      ) : (
        <FeedList>
          {visible.map(entry => (
            <Row key={entry.id} onClick={() => handleRowClick(entry)}>
              <Glyph aria-hidden="true">
                <Icon icon={entry.glyph} />
              </Glyph>
              <Body>
                <RowHeader>
                  <RowTitle>{entry.title}</RowTitle>
                  <RowTime>{formatRelativeTime(entry.occurredAt)}</RowTime>
                </RowHeader>
                <RowText>{entry.body}</RowText>
              </Body>
            </Row>
          ))}
        </FeedList>
      )}
    </Section>
  )
}

export default ActivityFeed
